import React, { useContext } from "react";
import IOrder from "../../../models/IOrder";
import styled from "styled-components";
import OwLabel from "../../shared/OwLabel/OwLabel";
import UserService from "../../../services/UserService";
import OwS3ImageViewer from "../../shared/OwS3ImageViewer/OwS3ImageViewer";
import IImageSizeEnum from "../../../models/IImageSizeEnum";
import { useNavigate } from "react-router-dom";
import * as _ from "lodash-es";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import VendorContext from "../../../contexts/VendorContext";
import UiContext from "../../../contexts/UiContext";
import HelperService from "../../../services/HelperService";
import CartService from "../../../services/CartService";
import { KitItemsDisplay } from "../../shared/KitItemsDisplay/KitItemsDisplay";
import { ReorderConfirmModal } from "../ReorderConfirmModal/ReorderConfirmModal";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";

type Props = {
    order: IOrder;
};

export const EcommerceOrderDetailRow: React.FC<Props> = (props) => {
    const navigate = useNavigate();
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const vendorModel = useContext(VendorContext);
    const uiContext = useContext(UiContext);

    const viewOrderDetails = () => {
        // View Details
        navigate(`/ecomm/orders/details/${props.order.guid}`);
    };

    const viewInvoice = () => {
        // View Invoice
        navigate(`/ecomm/orders/invoice/${props.order.guid}`);
    };

    const closeModal = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const executeReorder = async (clearExistingItems: boolean) => {
        closeModal();
        uiContext.setActivityIndicator(true);

        try {
            const result = await CartService.createCartFromOrder(
                {
                    orderGuid: props.order.guid,
                    clearExistingItems,
                    skipUnavailableItems: true,
                },
                userModel,
                supplierModel,
                userModel.isSupplierUser ? undefined : vendorModel
            );

            // Update cart item count (exclude kit child items)
            const count = result.cart.Cart_Items?.filter((i) => !i.parent_cart_item_id).length || 0;
            uiContext.setCartItemCount(count);

            // Show success toast
            uiContext.notifyFn(null, {
                message: "reorder_success",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            });

            navigate("/ecomm");
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "reorder_failed";
            uiContext.notifyFn({
                eid: "",
                title: "error_reorder",
                message: errorMessage,
            });
        } finally {
            uiContext.setActivityIndicator(false);
        }
    };

    const showReorderConfirmModal = () => {
        const modalComponent = (
            <ReorderConfirmModal
                closeModalFn={closeModal}
                keepExistingFn={() => executeReorder(false)}
                clearCartFn={() => executeReorder(true)}
            />
        );

        uiContext.setModalComponent(modalComponent);
        uiContext.showModal(true);
    };

    const reOrder = () => {
        if (uiContext.cartItemCount > 0) {
            showReorderConfirmModal();
        } else {
            executeReorder(false);
        }
    };

    return (
        <EcommerceOrderDetailRowDiv>
            <EcommerceOrderDetailRowHeaderDiv>
                <div className={"col"}>
                    <div>
                        <OwLabel title={"order_placed"} isBold={true} />
                    </div>
                    <div>{UserService.parseDate(props.order.submit_date)}</div>
                </div>
                <div className={"col"}>
                    <div>
                        <OwLabel title={"status"} isBold={true} />
                    </div>
                    <div>{_.startCase(props.order.Order_State?.name.toLowerCase())}</div>
                </div>
                <div className={"col"}>
                    <div>
                        <OwLabel title={"total"} isBold={true} />
                    </div>
                    <div>
                        {HelperService.formatCurrency({
                            value: props.order.grand_total || 0,
                            currency: "USD",
                            precision: supplierModel.pricing_precision || 2,
                        })}
                    </div>
                </div>
                <div className={"col"}>
                    <div>
                        <OwLabel title={"ship_to"} isBold={true} />
                    </div>
                    <div>{props.order.Ship_To?.company_name || props.order.Ship_To?.name}</div>
                </div>
                <div className={"col"}>
                    <div>
                        <OwLabel title={"po_no"} isBold={true} />
                    </div>
                    <div>{props.order.po_no}</div>
                </div>
                {supplierModel.has_order_due_date ? (
                    <div className={"col"}>
                        <div>
                            <OwLabel title={"due_date"} isBold={true} />
                        </div>
                        <div>{UserService.parseDate(props.order.due_date)}</div>
                    </div>
                ) : null}
                <div className={"col-auto text-end"}>
                    <div>
                        <OwLabel title={"order_no"} postText={":"} className={"me-2"} isBold={true} />
                        {props.order.name}
                        {supplierModel.Supplier_Integration.has_qbd && props.order.qbd_RefNumber ? (
                            <>
                                <b className={"ms-2"}>QBE SO#:</b> {props.order.qbd_RefNumber}
                            </>
                        ) : (
                            ""
                        )}
                    </div>
                    <div>
                        <OwLabel title={"order_details"} fn={viewOrderDetails} /> | <OwLabel title={"invoice"} fn={viewInvoice} /> |{" "}
                        <OwLabel title="reorder" fn={reOrder} />
                    </div>
                </div>
            </EcommerceOrderDetailRowHeaderDiv>
            <EcommerceLineItemsContainerDiv>
                {props.order.Order_Items.map((orderItem) => {
                    return (
                        <div key={orderItem.guid}>
                            <div className={"d-flex mb-2"} key={orderItem.guid}>
                                <div className={"me-2 w-5r min-h-5r"}>
                                    <OwS3ImageViewer
                                        catalogItem={orderItem.Catalog_Item}
                                        isMicroView={true}
                                        attachmentIndex={0}
                                        imageIndex={IImageSizeEnum.THUMB}
                                        className={""}
                                        includeAllImages={false}
                                        hideEmptyImage={false}
                                    />
                                </div>
                                <div>
                                    <EcommerceLineItemInfoDiv>
                                        <div>{orderItem.Catalog_Item?.name}</div>
                                        <div>{orderItem.description || orderItem.Catalog_Item?.description}</div>
                                        <div className={"text-small"}>
                                            <OwLabel title={"sku"} /> {orderItem.Catalog_Item?.sku}
                                        </div>
                                    </EcommerceLineItemInfoDiv>
                                    <div>
                                        <KitItemsDisplay orderItem={orderItem} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </EcommerceLineItemsContainerDiv>
        </EcommerceOrderDetailRowDiv>
    );
};

const EcommerceOrderDetailRowDiv = styled.div`
    width: 100%;
    min-height: 3rem;
    border-radius: 0.5rem;
    border: 1px solid var(--main-text-color);
    margin-top: 3rem;
`;

const EcommerceOrderDetailRowHeaderDiv = styled.div`
    background-color: var(--secondary-bg-color);
    min-height: 2rem;
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    padding: 0.5rem;
    display: flex;
    justify-content: space-between;
`;

const EcommerceLineItemsContainerDiv = styled.div`
    padding: 0.5rem;
`;

const EcommerceLineItemInfoDiv = styled.div``;
